import type { Answer, Question, ScoreValue } from '@dwa/shared';
import { useState } from 'react';
import { useDebouncedCallback } from '../hooks/useDebouncedCallback';
import { RiskBadge } from './ui';
import { ScoreScale } from './ScoreScale';

// The parent renders this with `key={question.id}`, so it remounts per question
// and the notes initial value below is always correct.
export function QuestionCard({
  question,
  answer,
  onSave,
  incomplete = false,
  referenceLabel = 'Reference',
}: {
  question: Question;
  answer: Answer | undefined;
  onSave: (score: ScoreValue, notes: string) => void;
  incomplete?: boolean;
  /** Per-framework label for the reference row, e.g. "Databricks reference". */
  referenceLabel?: string;
}) {
  const [notes, setNotes] = useState(answer?.notes ?? '');
  // Guidance is collapsed by default so the card stays focused on scoring; the
  // full text always appears in the report regardless.
  const [showDetails, setShowDetails] = useState(false);
  const score = answer?.score;
  const commitNotes = useDebouncedCallback((next: string) => {
    if (score !== undefined) onSave(score, next);
  }, 500);

  return (
    <article
      id={`q-card-${question.id}`}
      className={`scroll-mt-24 rounded-xl border bg-climb-surface p-6 ${
        incomplete ? 'border-climb-coral ring-1 ring-climb-coral' : 'border-climb-line-dark'
      }`}
      aria-labelledby={`q-${question.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="font-mono text-sm text-climb-teal-soft">{question.id}</div>
        <RiskBadge risk={question.risk} />
      </div>
      <h3 id={`q-${question.id}`} className="mt-2 text-lg font-medium text-climb-paper">
        {question.question}
      </h3>

      <button
        type="button"
        onClick={() => setShowDetails((s) => !s)}
        aria-expanded={showDetails}
        aria-controls={`q-details-${question.id}`}
        className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-climb-cyan hover:underline"
      >
        {showDetails ? '▴ Hide details' : '▾ Details'}
      </button>

      {showDetails ? (
        <dl id={`q-details-${question.id}`} className="mt-3 space-y-2 text-sm">
          {question.intent ? (
            <div>
              <dt className="font-semibold text-climb-cyan">Why it matters</dt>
              <dd className="text-climb-paper/80">{question.intent}</dd>
            </div>
          ) : null}
          <div>
            <dt className="font-semibold text-climb-cyan">What good looks like</dt>
            <dd className="text-climb-paper/80">{question.whatGoodLooksLike}</dd>
          </div>
          {question.reference ? (
            <div>
              <dt className="font-semibold text-climb-cyan">{referenceLabel}</dt>
              <dd className="font-mono text-xs text-climb-teal-soft">{question.reference}</dd>
            </div>
          ) : null}
        </dl>
      ) : null}

      <div className="mt-4">
        {incomplete && (
          <p className="mb-1.5 text-sm font-medium text-climb-coral">Not answered yet. Select a score or N/A.</p>
        )}
        <ScoreScale name={`score-${question.id}`} value={score} onChange={(s) => onSave(s, notes)} />
      </div>

      <label className="mt-4 block">
        <span className="mb-1 block text-sm font-medium text-climb-paper">Notes / evidence</span>
        <textarea
          className="w-full rounded-lg border border-climb-line-dark bg-climb-surface-2 px-3 py-2 text-sm text-climb-paper outline-none focus:border-climb-cyan"
          rows={2}
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value);
            commitNotes(e.target.value);
          }}
          onBlur={() => {
            if (score !== undefined && notes !== (answer?.notes ?? '')) onSave(score, notes);
          }}
        />
      </label>
    </article>
  );
}
